import React, { useState } from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import { cardManager } from "../../../../../shared/core/service/services";
import type { BalanceAccount } from "../../../../../shared/domains/account/account";
import type { Card } from "../../../../../shared/domains/cards/card";
import { CardStatus } from "../../../../../shared/domains/cards/card";
import type { OverrideCardData } from "../../../../../shared/domains/cards/card-service";
import { useAsyncEffect } from "../../../../../shared/utils/utils";
import { AmountText } from "../../../common/amount-text";
import { theme } from "../../../styles/theme";
import { getCardConfigurations } from "./card-configurations";

interface CardVisualProps {
  card: Card;
  balance?: BalanceAccount | null;
  sensitiveData?: OverrideCardData | null;
}

/**
 * The card itself: its artwork, with the figures laid on a frosted plate the way the mobile
 * "Ma carte" screen shows them. The plate exists because card artwork can be busy, and white text
 * alone gets lost on it.
 */
export function CardVisual({ card, balance, sensitiveData }: Readonly<CardVisualProps>) {
  const { formatMessage, formatDate } = useIntl();
  const [cardImage, setCardImage] = useState("");

  useAsyncEffect(async () => {
    setCardImage(await cardManager.getCardImage(card.id));
  }, [card.id]);

  const { statusColor, statusText } = getCardConfigurations(card.status);
  const cardImageOverride = sensitiveData?.base64Image;
  const image = cardImageOverride || cardImage;

  // An active card needs no badge: the mockup keeps the artwork clean and the lock state is already
  // carried by the "Verrouillage temporaire" row underneath.
  const badgeText = card.virtual ? "card.virtual" : card.status !== CardStatus.Active ? statusText : undefined;
  // "Virtuelle" is a kind of card, not a status, so it keeps the neutral tone.
  const badgeColor = card.virtual ? theme.colors.gray[700] : statusColor;

  const panDisplay = sensitiveData?.panDisplay || card.panDisplay;

  return (
    <Frame>
      {image ? <Artwork src={`data:image/png;base64,${image}`} alt="" /> : <Skeleton />}
      {!cardImageOverride && (
        <DetailsPanel>
          <div>
            {!!balance && (
              <>
                <BalanceLabel>{formatMessage("cardsList.availableBalance")}</BalanceLabel>
                <BalanceValue amount={balance} />
              </>
            )}
          </div>
          <div>
            {!!card.embossedName && <HolderName>{card.embossedName}</HolderName>}
            <MetaRow>
              {!!panDisplay && <Meta>{panDisplay}</Meta>}
              {!!card.expirationDate && (
                <Meta>{formatDate(card.expirationDate, { month: "2-digit", year: "2-digit", timeZone: "UTC" })}</Meta>
              )}
              {!!sensitiveData?.cvv && (
                <Meta>
                  {formatMessage("cardsList.details.cvv")} {sensitiveData.cvv}
                </Meta>
              )}
            </MetaRow>
          </div>
        </DetailsPanel>
      )}
      {/* After the panel, which covers the same corner: a status the holder must read wins over it. */}
      {!!badgeText && (
        <Badge color={badgeColor}>
          <BadgeLabel>{formatMessage(badgeText)}</BadgeLabel>
        </Badge>
      )}
    </Frame>
  );
}

/* The artwork is the card's own face, so the frame keeps a card's proportions rather than crop it. */
const Frame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1.586;
  border-radius: 12px;
  overflow: hidden;
`;

const Artwork = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Skeleton = styled.div`
  position: absolute;
  inset: 0;
  background-color: #e8e8e8;
`;

const Badge = styled.div<{ color?: string }>`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 3px 10px;
  border-radius: 10px;
  background-color: ${(props) => props.color};
`;

const BadgeLabel = styled.span`
  ${theme.boldText};
  font-size: 11px;
  color: #ffffff;
`;

const DetailsPanel = styled.div`
  position: absolute;
  inset: 12px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px;
  border-radius: 10px;

  /* The blur alone still averages out bright artwork, so a light scrim guarantees the contrast. */
  background-color: rgba(11, 41, 62, 0.2);
  backdrop-filter: blur(12px);
`;

const BalanceLabel = styled.div`
  ${theme.mediumText};
  font-size: 14px;
  line-height: 18px;
  color: #ffffff;
`;

const BalanceValue = styled(AmountText)`
  ${theme.blackText};
  margin-top: 2px;
  font-size: 33px;
  color: #ffffff;
`;

const HolderName = styled.div`
  ${theme.mediumText};
  font-size: 14px;
  line-height: 18px;
  color: #ffffff;
`;

const MetaRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 14px;
  margin-top: 2px;
`;

const Meta = styled.span`
  ${theme.text};
  font-size: 12px;
  line-height: 15px;
  color: #ffffff;
`;
